// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import loadInvoicePayment from "@adl/App/Handlers/InvoicePayment/LoadInvoicePayment";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getInvoicePaymentDetailsStateById } from "@adl/App/Store/Selectors/InvoicePaymentConfirmationSelectors";

import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@adl/Overrides/Components/SmallHeadingAndText";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ id?: string }>(search);
    const invoicePaymentId = parsedQuery.id;
    const { isLoading, value: invoicePaymentDetails } = getInvoicePaymentDetailsStateById(state, invoicePaymentId);
    return {
        invoiceDetailsPage: getPageLinkByPageType(state, "InvoiceDetailsPage"),
        language: state.context.session.language,
        invoicePaymentDetails,
        isLoading,
        invoicePaymentId,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = { loadInvoicePayment };

interface InvoicePaymentConfirmationStyles {
    invoicePaymentGridContainer?: GridContainerProps;
    invoicePaymentInformationGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    invoicesContainer?: InjectableCss;
    invoicesHeadeing?: TypographyPresentationProps;
    invoicesValue?: LinkPresentationProps;
}

export const invoicePaymentConfirmationStyles: InvoicePaymentConfirmationStyles = {};

const styles = invoicePaymentConfirmationStyles;

const InvoicePaymentConfirmationWidget: FC<Props> = ({
    invoicePaymentDetails,
    invoiceDetailsPage,
    loadInvoicePayment,
    invoicePaymentId,
    isLoading,
    language,
}: Props) => {
    useEffect(() => {
        if (invoicePaymentId) {
            loadInvoicePayment({ id: invoicePaymentId });
        }
    }, [invoicePaymentId]);

    if (!invoicePaymentId || !invoicePaymentDetails) {
        return null;
    }

    return (
        <LoadingOverlay loading={isLoading}>
            <GridContainer {...styles.invoicePaymentGridContainer}>
                {invoicePaymentDetails?.date && (
                    <GridItem {...styles.invoicePaymentInformationGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Invoice Payment Date")}
                            text={getLocalizedDateTime({
                                dateTime: new Date(invoicePaymentDetails?.date),
                                language,
                            })}
                        />
                    </GridItem>
                )}
                {invoicePaymentDetails?.paymentMethodName && (
                    <GridItem {...styles.invoicePaymentInformationGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Payment Method Name")}
                            text={invoicePaymentDetails?.paymentMethodName}
                        />
                    </GridItem>
                )}
                {invoicePaymentDetails?.totalAmount && (
                    <GridItem {...styles.invoicePaymentInformationGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Total Amount")}
                            text={invoicePaymentDetails?.totalAmount}
                        />
                    </GridItem>
                )}
                {invoicePaymentDetails?.isCreditCardPayment && (
                    <>
                        {invoicePaymentDetails?.cardNumber && (
                            <GridItem {...styles.invoicePaymentInformationGridItem}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Card Number")}
                                    text={invoicePaymentDetails?.cardNumber}
                                />
                            </GridItem>
                        )}
                        {invoicePaymentDetails?.cardType && (
                            <GridItem {...styles.invoicePaymentInformationGridItem}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Card Type")}
                                    text={invoicePaymentDetails?.cardType}
                                />
                            </GridItem>
                        )}
                    </>
                )}
                {invoicePaymentDetails?.customerComment && (
                    <GridItem {...styles.invoicePaymentInformationGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Customer Comment")}
                            text={invoicePaymentDetails?.customerComment}
                        />
                    </GridItem>
                )}
                {invoicePaymentDetails?.invoices && (
                    <GridItem {...styles.invoicePaymentInformationGridItem}>
                        <Typography {...styles.invoicesHeadeing}>{translate("Invoices")}</Typography>
                        <StyledWrapper {...styles.invoicesContainer}>
                            {invoicePaymentDetails?.invoices?.split(",")?.map((invoice: string) => (
                                <Link
                                    href={`${invoiceDetailsPage?.url}?invoiceNumber=${invoice}`}
                                    {...styles.invoicesValue}
                                    key={invoice}
                                >
                                    {invoice}
                                </Link>
                            ))}
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoicePaymentConfirmationWidget),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Invoice Payment Confirmation Widget",
        allowedContexts: ["InvoicePaymentConfirmationPage" as any],
    },
};

export default widgetModule;
